import type { Money } from './api/types';
import { convertPriceCeil, formatCurrency, FALLBACK_RATES } from './currency';
import { useCurrency } from '~/stores/useCurrency';

/**
 * Single source of truth for formatting customer-facing prices.
 * Converts to the active selected currency with CEIL rounding unless
 * an overrideCurrency is explicitly provided.
 */
export function formatMoney(money: Money, overrideCurrency?: string): string {
    const state = useCurrency.getState();
    const targetCurrency = overrideCurrency ?? state.selectedCurrency ?? 'USD';
    const rates = state.rates ?? FALLBACK_RATES;

    const sourceCurrency = money.currencyCode || 'USD';
    const converted = convertPriceCeil(money.amount, sourceCurrency, targetCurrency, rates);

    return formatCurrency(converted, targetCurrency);
}

/**
 * Formats an order's total in the currency in which it was booked.
 * Preserves the exact booked currency and amount (e.g. USD, EUR, GBP, INR)
 * unless an explicit overrideCurrency is provided.
 */
export function formatOrderMoney(
    order: {
        total: Money;
        usdTotal?: Money;
        inrTotal?: Money;
    },
    overrideCurrency?: string
): string {
    const bookedCurrency = order.total?.currencyCode || 'USD';
    const targetCurrency = overrideCurrency ?? bookedCurrency;

    // If target matches the booked currency, display the booked total directly
    if (targetCurrency === bookedCurrency && order.total?.amount) {
        return formatCurrency(order.total.amount, bookedCurrency);
    }

    // If target is USD and usdTotal is stored, use it directly to prevent rounding drift
    if (targetCurrency === 'USD' && order.usdTotal?.amount && Number(order.usdTotal.amount) > 0) {
        return formatCurrency(order.usdTotal.amount, 'USD');
    }

    // If target is INR and inrTotal is stored, use it directly
    if (targetCurrency === 'INR' && order.inrTotal?.amount && Number(order.inrTotal.amount) > 0) {
        return formatCurrency(order.inrTotal.amount, 'INR');
    }

    // If usdTotal is available, prefer converting from usdTotal as clean base
    if (order.usdTotal?.amount && Number(order.usdTotal.amount) > 0) {
        return formatMoney(order.usdTotal, targetCurrency);
    }

    return formatMoney(order.total, targetCurrency);
}

export function computeDiscountAmount(
    lowestPrice: Money,
    retailPrice: Money,
    targetCurrency?: string
): number {
    const state = useCurrency.getState();
    const target = targetCurrency ?? state.selectedCurrency ?? 'USD';
    const rates = state.rates ?? FALLBACK_RATES;

    const low = convertPriceCeil(lowestPrice.amount, lowestPrice.currencyCode || 'USD', target, rates);
    const retail = convertPriceCeil(retailPrice.amount, retailPrice.currencyCode || 'USD', target, rates);

    return Math.max(0, Math.round(retail - low));
}

/** Same-currency addition only — throws rather than silently mixing
 * currencies, since a wrong total here is a customer-facing price. */
export function addMoney(a: Money, b: Money): Money {
    if (a.currencyCode !== b.currencyCode) {
        throw new Error(`Cannot add ${a.currencyCode} to ${b.currencyCode}`);
    }
    return { amount: (Number(a.amount) + Number(b.amount)).toFixed(2), currencyCode: a.currencyCode };
}

export * from './currency';
